import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import CountryCodeSelect from './CountryCodeSelect';

/*
 * Country code + local number, emitting one E.164 string.
 *
 * Twilio silently drops anything that isn't E.164, so a bare local number never
 * reaches its owner — which for a phone-verification flow means the account can
 * never be verified. Splitting the field in two makes the country code
 * impossible to forget, rather than something the user has to know to type.
 *
 * Countries come from /geo/countries, the same seeded table the location picker
 * uses, so every country the platform knows about can sign up.
 */

// Shared across every instance on the page; the list is static reference data.
let countriesPromise = null;
const loadCountries = () => {
  if (!countriesPromise) {
    countriesPromise = api.get('/geo/countries')
      .then(r => r.data.filter(c => c.phone_code))
      .catch(e => { countriesPromise = null; throw e; });
  }
  return countriesPromise;
};

/** Split a stored E.164 number into its dialling code and the rest. */
export function splitE164(value, countries) {
  if (!value || !value.startsWith('+') || !countries.length) return null;
  const digits = value.slice(1);
  // Longest code first: "+1721..." is Sint Maarten, not the US followed by 721.
  const match = [...countries]
    .sort((a, b) => b.phone_code.length - a.phone_code.length)
    .find(c => digits.startsWith(c.phone_code));
  if (!match) return null;
  return { code: match.phone_code, local: digits.slice(match.phone_code.length) };
}

export default function PhoneInput({
  label = 'Mobile number',
  value,                 // full E.164 string, e.g. "+919876543210"
  onChange,              // called with the full E.164 string (or '' when empty)
  error,
  defaultCountryIso2 = 'IN',
  disabled,
}) {
  const [countries, setCountries] = useState([]);
  const [code, setCode] = useState('');
  // Tracked alongside the code because +1 covers 25 countries — the code
  // alone cannot say which flag to show.
  const [iso2, setIso2] = useState('');
  const [local, setLocal] = useState('');
  const [focused, setFocused] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    loadCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  // Seed the two halves once the country list is available: from `value` when
  // editing an existing number, otherwise from the default country.
  useEffect(() => {
    if (!countries.length || hydrated) return;
    const parsed = splitE164(value, countries);
    if (parsed) {
      setCode(parsed.code);
      setLocal(parsed.local);
      // Prefer the default country when it shares the parsed code, so an
      // Indian user editing "+1..." doesn't silently become American Samoa.
      const owner = countries.find(c => c.phone_code === parsed.code && c.iso2 === defaultCountryIso2)
        || countries.find(c => c.phone_code === parsed.code);
      setIso2(owner?.iso2 || '');
    } else {
      const fallback = countries.find(c => c.iso2 === defaultCountryIso2) || countries[0];
      setCode(fallback?.phone_code || '');
      setIso2(fallback?.iso2 || '');
    }
    setHydrated(true);
  }, [countries, value, hydrated, defaultCountryIso2]);

  const emit = (nextCode, nextLocal) => {
    onChange(nextLocal ? `+${nextCode}${nextLocal}` : '');
  };

  const pickCountry = (country) => {
    setCode(country.phone_code);
    setIso2(country.iso2);
    emit(country.phone_code, local);
  };

  const typeLocal = (e) => {
    // Digits only: users paste numbers with spaces, dashes and brackets, and a
    // leading 0 is a domestic trunk prefix that must not survive into E.164.
    const next = e.target.value.replace(/\D/g, '').replace(/^0+/, '');
    setLocal(next);
    emit(code, next);
  };

  // Several countries share a dialling code (+1 covers 25), so the option label
  // carries the country name and the value has to stay unique per option.
  const options = useMemo(
    () => [...countries].sort((a, b) => a.name.localeCompare(b.name)),
    [countries],
  );

  const borderColor = error ? 'var(--red)' : focused ? 'var(--red)' : 'var(--gray-200)';

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block', fontSize: '13.5px', fontWeight: 600,
          marginBottom: '6px', color: 'var(--gray-700)',
        }}>
          {label}
        </label>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <CountryCodeSelect
          countries={options}
          value={code}
          selectedIso2={iso2}
          onSelect={pickCountry}
          disabled={disabled || !countries.length}
          borderColor={borderColor}
        />
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="9876543210"
          value={local}
          onChange={typeLocal}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, minWidth: 0, padding: '11px 14px',
            border: `1.5px solid ${borderColor}`, borderRadius: '8px',
            outline: 'none', background: 'var(--white)',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            boxShadow: focused ? '0 0 0 3px rgba(200,16,46,0.12)' : 'none',
            fontSize: '14px', color: 'var(--gray-900)',
          }}
        />
      </div>
      {error
        ? <p style={{ fontSize: '12.5px', color: 'var(--red)', marginTop: '5px', fontWeight: 500 }}>{error}</p>
        : <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '5px' }}>
            We text a verification code here, so pick the country your number is registered in.
          </p>}
    </div>
  );
}
